import { styled } from "@linaria/react";
import React from "react";
import { useQuery } from "@tanstack/react-query";
import useToasts from "@ui/Toast/useToasts";
import t from "../../../../../i18n";
import SmallCollapse from "@ui/Collapse/SmallCollapse";
import ModalForm from "@ui/Modal/Form";
import { color, fonts } from "@ui";
import { FileCopyLine } from "@ui/IconV2/react";
import Text from "@ui/Text";
import AdditionnalMessage from "@ui/AdditionnalMessage";
import Spinner from "@ui/Spinner";
import call from "../../../../../api/call";
import type { Quantity } from "../../../../../api/useQuantities";
const DetailNote = styled.div`
  margin: 10px 0;
`;

const CopyContainer = styled.div`
  display: flex;
  gap: 0.2em;
  text-decoration: underline;
  margin-top: 10px;
  color: ${color.titanium600};
  font: ${fonts.body2SemiBold};
  cursor: pointer;
`;

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  padding: 24px 0;
`;

const DELETE_IMPACT_PATH =
  "/projects/:projectId(uuid)/quantities/:quantityId(uuid)/delete-impact" as const;

const PrimaryList = styled.ul`
  list-style-type: initial;
  margin-left: 20px;

  li {
    font: ${fonts.body3};

    span {
      font: ${fonts.body1};
    }
  }
`;

interface DependencyTreeFormatted {
  name: string;
  formula: string | null;
  usedBy: DependencyTreeFormatted[];
}

export interface DependencyTree {
  id: string;
  name: string;
  formula?: string | null;
  trees: DependencyTree[];
}

interface P {
  projectId: string;
  onRequestClose: () => void;
  handleSubmit: () => Promise<boolean>;
  quantity: Quantity;
}

export const mapToExportFormat = (
  tree: DependencyTree,
  quantities: Quantity[] = [],
): DependencyTreeFormatted => ({
  name: tree.name,
  formula: tree.formula ?? quantities.find(q => q.id === tree.id)?.formula ?? null,
  usedBy: tree.trees.map(p => mapToExportFormat(p, quantities)),
});

const ConfirmDelete = ({ projectId, onRequestClose, handleSubmit, quantity }: P) => {
  const { addToast } = useToasts();
  const {
    data: deleteImpactResponse,
    isLoading: isLoadingDeleteImpact,
    isError: hasDeleteImpactError,
  } = useQuery({
    queryKey: [DELETE_IMPACT_PATH, projectId, quantity.id],
    queryFn: () =>
      call("GET", DELETE_IMPACT_PATH, {
        routeParams: { projectId, quantityId: quantity.id },
      }),
    retry: false,
    suspense: false,
  });
  const deleteImpactBody = deleteImpactResponse?.body ?? null;

  React.useEffect(() => {
    if (!hasDeleteImpactError) return;

    addToast("error", t("errors.backend_error"));
    onRequestClose();
  }, [addToast, hasDeleteImpactError, onRequestClose]);

  const hasImpactOnSetups = deleteImpactBody?.hasImpactOnSetups ?? false;
  const isUsedInFormula = deleteImpactBody?.isUsedInFormula ?? false;
  const impactedSetupNames = deleteImpactBody?.impactedSetupNames ?? [];
  const formulaDependencies = React.useMemo(
    () =>
      deleteImpactBody?.impactedQuantityTree ?? {
        id: quantity.id,
        name: quantity.name,
        formula: quantity.formula,
        trees: [],
      },
    [deleteImpactBody, quantity.id, quantity.name, quantity.formula],
  );

  const [expanded, setExpanded] = React.useState<boolean>(false);

  const getChildren = React.useCallback(
    (quantity: string) => {
      const stack = [formulaDependencies];
      while (stack.length > 0) {
        const tmp = stack.pop();
        if (tmp?.name === quantity) return tmp.trees;
        if (tmp?.trees) stack.push(...tmp.trees);
      }
      return [];
    },
    [formulaDependencies],
  );
  const dependenciesLevel1 = formulaDependencies.trees.map((c: DependencyTree) => c.name);
  const deletedQuantitiesCount = React.useMemo(() => {
    let count = 0;
    const stack = [formulaDependencies];
    while (stack.length > 0) {
      const tmp = stack.pop();
      if (tmp?.trees) stack.push(...tmp.trees);
      count++;
    }
    return count;
  }, [formulaDependencies]);

  const dependenciesJsonified = React.useMemo(
    () => mapToExportFormat(formulaDependencies),
    [formulaDependencies],
  );
  const handleCopy = React.useCallback(() => {
    void window.navigator.clipboard.writeText(JSON.stringify(dependenciesJsonified));
    addToast("info", t("commons.copyDetail.toast"));
  }, [addToast, dependenciesJsonified]);

  const childrenCount = React.useCallback((node: DependencyTree) => {
    const stack = [...node.trees];
    let count = 0;
    while (stack.length > 0) {
      const current = stack.pop();
      count += 1;
      if (current?.trees) {
        stack.push(...current.trees);
      }
    }
    return count;
  }, []);

  const isSensor = quantity.sensor ?? null;

  return (
    <ModalForm
      onRequestClose={onRequestClose}
      onSubmit={
        hasImpactOnSetups || isLoadingDeleteImpact || hasDeleteImpactError ? false : handleSubmit
      }
      okButtonName={t("modal.delete")}
      okButtonVariant="destructive"
    >
      {isLoadingDeleteImpact ? (
        <LoadingContainer>
          <Spinner width={32} height={32} />
        </LoadingContainer>
      ) : hasImpactOnSetups ? (
        <Text>{t("project.quantities.delete.forbidden", quantity.name, impactedSetupNames)}</Text>
      ) : (
        <>
          <Text>{t("project.quantities.delete.body", quantity.name)}</Text>
          {isUsedInFormula ? (
            <>
              <span>
                {t("project.quantities.delete.formula.body.quantities", {
                  quantityName: formulaDependencies.name,
                  impactedQuantitiesCount: deletedQuantitiesCount - 1,
                })}
              </span>

              <SmallCollapse
                title={t("commons.details")}
                expanded={expanded}
                handleClick={() => setExpanded(!expanded)}
              >
                <p>
                  <b>
                    {t("project.quantities.delete.formula.details.body", {
                      impactedCount: deletedQuantitiesCount - 1, // -1 because we dont want to count the targeted formula
                    })}
                  </b>
                </p>
                <PrimaryList>
                  {dependenciesLevel1.map((dependency: string) => (
                    <li key={dependency}>
                      <span>
                        {t("project.quantities.delete.formula.details.label.level1", {
                          quantityName: dependency,
                          dependencyCount: getChildren(dependency).length,
                        })}
                      </span>
                      <PrimaryList>
                        {getChildren(dependency).map((child: DependencyTree) => (
                          <li key={child.id}>
                            <span>
                              {t("project.quantities.delete.formula.details.label.level2", {
                                quantityName: child.name,
                                dependencyCount: childrenCount(child),
                              })}
                            </span>
                          </li>
                        ))}
                      </PrimaryList>
                    </li>
                  ))}
                </PrimaryList>

                <DetailNote>
                  {t("project.quantities.delete.formula.nota", {
                    quantityName: formulaDependencies.name,
                  })}
                </DetailNote>
              </SmallCollapse>

              <CopyContainer onClick={handleCopy} role="button">
                <FileCopyLine color={color.titanium600} /> {t("commons.copyDetail")}
              </CopyContainer>
            </>
          ) : null}

          {isSensor && (
            <AdditionnalMessage
              status="WARNING"
              text={t("project.quantities.delete.additionnal_message.sensor")}
            />
          )}
        </>
      )}
    </ModalForm>
  );
};

export default React.memo(ConfirmDelete);
